import { sectionsApi } from 'entities/section/api/sectionsApi';
import filter from 'lodash-es/filter';
import find from 'lodash-es/find';
import map from 'lodash-es/map';
import { makeAutoObservable, runInAction } from 'mobx';
import {
  ExplorerModelInterface,
  ExplorerEntityTypes,
  ContentItem,
  SortContentKey,
  SortContentOrder,
  ContentFolderItem,
} from 'entities/types';
import { FolderModel } from 'entities/folder/model/folderModel';

export type SectionData = {
  id: string;
  name: string;
  description?: string;
  image_src: string;
  last_change_date: string;
  files_amount: number;
  links?: SectionLink[];
};

export type SectionLink = {
  icon: SectionLinkIcon;
  text: string;
  src: string;
};

export enum SectionLinkIcon {
  JPG = 'jpg',
  PPT = 'ppt',
}

export type UpdateSectionParams = {
  name?: string;
  image_src?: File;
};

export type CreateSectionParams = UpdateSectionParams;

export class SectionModel implements ExplorerModelInterface {
  public type = ExplorerEntityTypes.SECTION as const;
  public isPending = false;
  public isError = false;

  public content: ContentItem[] | null = null;
  private _folders: FolderModel[] | null = null;

  private _sortKey: SortContentKey = SortContentKey.LAST_CHANGE_DATE;
  public get sortKey() {
    return this._sortKey;
  }
  public set sortKey(val: SortContentKey) {
    this._sortKey = val;
  }
  private _sortOrder: SortContentOrder = SortContentOrder.DESC;
  public get sortOrder() {
    return this._sortOrder;
  }
  public set sortOrder(val: SortContentOrder) {
    this._sortOrder = val;
  }

  public data: SectionData | null = null;
  private _id: string;
  public get id() {
    return this._id;
  }
  public get name() {
    return this.data?.name;
  }

  public get shortFolders() {
    return filter(
      this.content,
      (item) => item.type === ExplorerEntityTypes.FOLDER
    );
  }

  constructor(idOrData: SectionData | string) {
    if (typeof idOrData === 'string') {
      this._id = idOrData;
    } else {
      this._id = idOrData.id;
      this.data = idOrData;
    }
    makeAutoObservable(this);
  }

  public findFolderById(id: string) {
    return find(this._folders, (s) => s.id === id);
  }

  public async fetch({ in_trash }: { in_trash: string | null }) {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      const data = await sectionsApi.fetch(this._id, { in_trash });

      runInAction(() => {
        this.data = data;
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      console.error(e);
    }
  }

  public async update(params: UpdateSectionParams) {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      const data = await sectionsApi.update(this._id, params);

      runInAction(() => {
        this.data = data;
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      throw e;
    }
  }

  public async delete() {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      await sectionsApi.delete(this._id);

      runInAction(() => {
        this.data = null;
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      throw e;
    }
  }

  public async fetchFolders({ in_trash }: { in_trash: string | null }) {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      const _folders = await sectionsApi.fetchFolders(this._id, { in_trash });

      runInAction(() => {
        this._folders = map(_folders, (folder) => new FolderModel(folder));
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      console.error(e);
    }
  }

  public async getContent({ in_trash }: { in_trash: string | null }) {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      const res = await sectionsApi.getContent(this._id, {
        in_trash,
        sort_by: this._sortKey,
        sort_direction: this._sortOrder,
      });
      this.content = res;

      runInAction(() => {
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      console.error(e);
    }
  }

  public localAddFolder(folderModel: FolderModel) {
    this._folders = [...(this._folders || []), folderModel];

    const newFolderContent: ContentFolderItem = {
      id: folderModel.id,
      name: folderModel.name || '',
      type: ExplorerEntityTypes.FOLDER,
      last_change_date: folderModel.data?.last_change_date || '',
      files_amount: folderModel.data?.files_amount || 0,
      section_id: folderModel.data?.section_id!,
      parent_id: folderModel.data?.parent_id!,
    };

    runInAction(() => {
      this.content = [newFolderContent, ...(this.content || [])];
    });
  }
  public localDeleteFolders(ids: string[]) {
    if (this._folders) {
      this._folders = filter(
        this._folders,
        (folder) => !ids.includes(folder.id)
      );
    }

    runInAction(() => {
      this.content = filter(
        this.content || [],
        (item) =>
          !(item.type === ExplorerEntityTypes.FOLDER && ids.includes(item.id))
      );
    });
  }

  public localDeleteFile(id: string) {
    this.content = filter(
      this.content || [],
      (item) => !(item.type === ExplorerEntityTypes.FILE && item.id === id)
    );
  }

  public localDeleteTable(id: string) {
    this.content = filter(
      this.content || [],
      (item) => !(item.type === ExplorerEntityTypes.TABLE && item.id === id)
    );
  }
}
